import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Trash2, Star } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import StarRating from "../../components/StarRating";
import { resolveImageUrl } from "../../components/ImageUpload";

const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Hidden" },
  { key: "", label: "All" },
];

export default function AdminReviews() {
  const [filter, setFilter] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : "";
      const r = await api.get(`/admin/reviews${params}`);
      setReviews(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const moderate = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status });
      toast.success(status === "approved" ? "Review approved" : status === "rejected" ? "Review hidden" : "Marked pending");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try { await api.delete(`/reviews/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div data-testid="admin-reviews">
      <div className="overline text-zinc-500">Community</div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-2">Review moderation</h1>
      <p className="text-sm text-zinc-500 mb-6">
        New reviews stay hidden until you approve them. Approved reviews show on the flavour page and adjust the average rating.
      </p>

      <div className="flex gap-2 mb-6 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.key || "all"} onClick={() => setFilter(f.key)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                    filter === f.key
                      ? "bg-[#F74B64] text-white border-[#F74B64]"
                      : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                  }`} data-testid={`filter-${f.key || "all"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 p-10 text-center">
          <Star size={32} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-500">No reviews in this view.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`admin-review-${r.id}`}>
              <div className="flex items-start gap-3 mb-3 flex-wrap">
                {r.user_avatar_url ? (
                  <img src={resolveImageUrl(r.user_avatar_url)} alt={r.user_name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#F74B64] text-white flex items-center justify-center font-bold flex-shrink-0">
                    {(r.user_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{r.user_name}</span>
                    <span className="text-xs text-zinc-400">→</span>
                    <span className="font-semibold text-sm text-[#F74B64]">{r.product_name || r.product_id.slice(0, 8)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <StarRating rating={r.rating} size={12} />
                    <span className="text-[10px] text-zinc-400">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <StatusPill status={r.status} />
              </div>
              {r.comment && <p className="text-sm text-zinc-700 break-words">{r.comment}</p>}

              <div className="mt-4 flex flex-wrap gap-2">
                {r.status !== "approved" && (
                  <button onClick={() => moderate(r.id, "approved")}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-100 text-green-700 hover:bg-green-200"
                          data-testid={`approve-${r.id}`}>
                    <CheckCircle2 size={12} /> Approve
                  </button>
                )}
                {r.status !== "rejected" && (
                  <button onClick={() => moderate(r.id, "rejected")}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200"
                          data-testid={`hide-${r.id}`}>
                    <XCircle size={12} /> Hide
                  </button>
                )}
                <button onClick={() => remove(r.id)}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full text-red-600 hover:bg-red-50"
                        data-testid={`delete-${r.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { label: "Pending", bg: "bg-amber-100", color: "text-amber-700" },
    approved: { label: "Approved", bg: "bg-green-100", color: "text-green-700" },
    rejected: { label: "Hidden", bg: "bg-red-100", color: "text-red-700" },
  };
  const m = map[status] || map.pending;
  return <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${m.bg} ${m.color} flex-shrink-0`}>{m.label}</span>;
}
